import { SEOConfig } from '../config/types';

/**
 * Converts a relative URL to an absolute URL
 */
function getAbsoluteUrl(url: string): string {
  // If already absolute, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Get the current origin
  const origin = window.location.origin;
  
  // Ensure the URL starts with /
  const path = url.startsWith('/') ? url : `/${url}`;
  
  return `${origin}${path}`;
}

/**
 * Updates HTML metadata tags based on SEO configuration
 */
export function updateMetadata(seo: SEOConfig): void {
  // Update document title
  if (seo.title) {
    document.title = seo.title;
  }

  // Update or create meta description
  updateMetaTag('description', seo.description);
  
  // Update or create meta keywords
  if (seo.keywords && seo.keywords.length > 0) {
    updateMetaTag('keywords', seo.keywords.join(', '));
  }

  // Open Graph tags
  if (seo.ogTitle) {
    updateMetaTag('og:title', seo.ogTitle, 'property');
  }
  if (seo.ogDescription) {
    updateMetaTag('og:description', seo.ogDescription, 'property');
  }
  if (seo.ogImage) {
    // Convert to absolute URL for Open Graph
    const absoluteImageUrl = getAbsoluteUrl(seo.ogImage);
    updateMetaTag('og:image', absoluteImageUrl, 'property');
  }
  if (seo.ogUrl) {
    updateMetaTag('og:url', seo.ogUrl, 'property');
  }
  updateMetaTag('og:type', 'website', 'property');

  // Twitter Card tags
  if (seo.twitterCard) {
    updateMetaTag('twitter:card', seo.twitterCard);
  }
  if (seo.twitterTitle) {
    updateMetaTag('twitter:title', seo.twitterTitle);
  }
  if (seo.twitterDescription) {
    updateMetaTag('twitter:description', seo.twitterDescription);
  }
  if (seo.twitterImage) {
    // Convert to absolute URL for Twitter
    const absoluteImageUrl = getAbsoluteUrl(seo.twitterImage);
    updateMetaTag('twitter:image', absoluteImageUrl);
  }
}

/**
 * Helper function to update or create a meta tag
 */
function updateMetaTag(name: string, content: string, attribute: 'name' | 'property' = 'name'): void {
  // Safety check: ensure document.head exists
  if (!document.head) {
    console.warn('document.head is not available, skipping metadata update');
    return;
  }
  
  // Try to find existing tag
  let metaTag = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
  
  if (!metaTag) {
    // Create new meta tag if it doesn't exist
    metaTag = document.createElement('meta');
    metaTag.setAttribute(attribute, name);
    document.head.appendChild(metaTag);
  }
  
  // Update content
  metaTag.setAttribute('content', content);
}
